/**
 * Utilities for handling redirect flow when users access protected routes while unauthenticated
 */

const REDIRECT_KEY = 'pds_redirect_after_login';
const AUTHENTICATED_SESSION_KEY = 'pds_authenticated_session';

const PUBLIC_PATHS = new Set([
  '/',
  '/privacy-policy',
  '/terms-of-service',
  '/subscription-agreement',
]);

/**
 * Check whether a route is available without authentication.
 * Accepts both canonical paths and their trailing-slash variants.
 */
export function isPublicPath(path: string): boolean {
  const pathname = path.split(/[?#]/, 1)[0];
  const normalizedPath = pathname.length > 1
    ? pathname.replace(/\/+$/, '')
    : pathname;

  return PUBLIC_PATHS.has(normalizedPath);
}

/**
 * Remember that this browser tab has had an authenticated PDS session.
 * sessionStorage intentionally scopes this to the current tab so a fresh tab
 * opened from an external link can still return to the requested destination.
 */
export function markAuthenticatedSession(): void {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(AUTHENTICATED_SESSION_KEY, 'true');
  } catch {
    // Storage can be unavailable in some browser privacy modes.
  }
}

export function clearAuthenticatedSession(): void {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.removeItem(AUTHENTICATED_SESSION_KEY);
  } catch {
    // Storage can be unavailable in some browser privacy modes.
  }
}

function hadAuthenticatedSession(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return sessionStorage.getItem(AUTHENTICATED_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Build the login URL after authentication is lost.
 *
 * A previously authenticated tab is an expired session and should use the
 * normal post-login landing page. A fresh unauthenticated tab is treated as a
 * deep link and returns to the protected page after login.
 */
export function getLoginUrlAfterAuthFailure(currentPath: string): string {
  if (hadAuthenticatedSession()) return '/auth/login';

  return `/auth/login?redirect_url=${encodeURIComponent(currentPath)}`;
}

/**
 * Store the current URL path to redirect to after login
 * @param path - The path to redirect to after login. If not provided, uses current window.location.pathname + search
 */
export function storeRedirectUrl(path?: string): void {
  if (typeof window === 'undefined') return; // SSR guard
  
  const redirectPath = path || (window.location.pathname + window.location.search);
  localStorage.setItem(REDIRECT_KEY, redirectPath);
}

/**
 * Get the stored redirect URL path
 * @returns The stored redirect path or null if none exists
 */
export function getRedirectUrl(): string | null {
  if (typeof window === 'undefined') return null; // SSR guard
  
  return localStorage.getItem(REDIRECT_KEY);
}

/**
 * Clear the stored redirect URL
 */
export function clearRedirectUrl(): void {
  if (typeof window === 'undefined') return; // SSR guard
  
  localStorage.removeItem(REDIRECT_KEY);
}

/**
 * Get the redirect path that should be used after login
 * @param defaultPath - Default path to redirect to if no stored URL exists (default: '/app/')
 * @returns The path to redirect to
 */
export function getPostLoginRedirectPath(defaultPath: string = '/app/'): string {
  const redirectPath = getRedirectUrl();
  
  if (redirectPath) {
    clearRedirectUrl();
    
    // Validate that the redirect path is safe (starts with /)
    if (redirectPath.startsWith('/') && !redirectPath.startsWith('//')) {
      return redirectPath;
    } else {
      console.warn('Invalid redirect path stored:', redirectPath);
      clearRedirectUrl();
    }
  }
  
  return defaultPath;
}

/**
 * Check if the current path represents an unauthenticated access attempt that should trigger a redirect flow
 * @param currentPath - The current pathname (defaults to window.location.pathname)
 * @returns true if this appears to be an unauthenticated access attempt
 */
export function shouldTriggerRedirectFlow(currentPath?: string): boolean {
  const path = currentPath || (typeof window !== 'undefined' ? window.location.pathname : '');
  
  // Don't trigger redirect flow if already on auth pages
  if (path.startsWith('/auth/')) return false;
  
  // Don't trigger for public pages
  if (isPublicPath(path)) return false;
  
  // Trigger redirect flow for app pages and other protected routes
  return path.startsWith('/app/') || path.startsWith('/staff/');
}
